import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faPlus, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { Controller } from "react-hook-form";
import { useFieldArray } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { Select } from "src/base-components/Select";
import { EnumColor } from "src/clients/features-control/api";
import { EventSchemaForm } from "src/eventsCatalogue/EventSchemaEditSidebar";

export const EnumValuesEditor = ({ property }: { property: string }) => {
  const { fields, append, remove, move } = useFieldArray<EventSchemaForm>({
    name: `properties.${property}.enum_schema.values`,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex((field) => field.id === active.id);
      const fieldToInsert = fields.findIndex((field) => field.id === over.id);
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
      }
    }
  };

  return (
    <div className="ml-2.5 flex flex-col gap-y-3 border-l border-gray-200 pl-4">
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((field) => field.id)}>
          {fields.map((field, index) => (
            <EnumValueFields
              key={field.id}
              id={field.id}
              immutable={!field._isNew}
              index={index}
              property={property}
              onRemove={remove}
            />
          ))}
        </SortableContext>
      </DndContext>
      <div>
        <Button
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() =>
            append(
              {
                color: EnumColor.GRAY,
                value: "",
                _isNew: true,
              },
              {
                focusName: `properties.${property}.enum_schema.values.${fields.length}.value`,
              },
            )
          }
        >
          Add value
        </Button>
      </div>
    </div>
  );
};

export const Colors = {
  [EnumColor.GRAY]: "text-gray-400",
  [EnumColor.INDIGO_LIGHT]: "text-indigo-500",
  [EnumColor.GREEN]: "text-green-500",
  [EnumColor.RED]: "text-red-500",
  [EnumColor.ORANGE]: "text-orange-500",
  [EnumColor.YELLOW]: "text-yellow-500",
};

export const enumColorOptions = Object.values(EnumColor).map((color) => ({
  key: color,
  value: (
    <div className="w-4">
      <Icon color={Colors[color]} icon={faCircle} size="3xs" />
    </div>
  ),
}));

const EnumValueFields = ({
  id,
  property,
  index,
  immutable,
  onRemove,
}: {
  id: string;
  property: string;
  index: number;
  immutable: boolean;
  onRemove: (index: number) => void;
}) => {
  const { listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} className="flex items-start gap-x-2" style={style}>
      <div className="pt-1">
        <ReorderHandle color="text-gray-500" listeners={listeners} />
      </div>
      <div>
        <Controller
          name={`properties.${property}.enum_schema.values.${index}.color`}
          render={({ field }) => (
            <Select
              options={enumColorOptions}
              placeholder="Select color"
              value={field.value}
              fullWidth
              onChange={field.onChange}
            />
          )}
        />
      </div>
      <div className="flex-1 self-start">
        <Controller<
          EventSchemaForm,
          `properties.${string}.enum_schema.values.${number}.value`
        >
          name={`properties.${property}.enum_schema.values.${index}.value`}
          render={({ field, fieldState }) => (
            <>
              <Input
                ref={field.ref}
                disabled={immutable}
                errored={!immutable && !!fieldState.error}
                value={field.value}
                fullWidth
                monospaced
                onChange={(e) => {
                  field.onChange(e.target.value);
                }}
              />
              {!immutable && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value, formValues) => {
              if (!value) return;

              const VALIDATION_ERROR =
                "Value must be a string in quotes or a number";
              try {
                const parsedValue = JSON.parse(value);
                if (
                  typeof parsedValue !== "string" &&
                  typeof parsedValue !== "number"
                ) {
                  return VALIDATION_ERROR;
                }
              } catch (error) {
                return VALIDATION_ERROR;
              }

              const allEnumValues =
                formValues.properties[property].enum_schema?.values ?? [];
              // Check for duplicate values
              const thereIsDuplicate = allEnumValues.some(
                (enumValue) =>
                  enumValue.value === value &&
                  enumValue !== allEnumValues[index],
              );
              if (thereIsDuplicate) {
                return "Value must be unique";
              }
            },
          }}
        />
      </div>
      <div className="pt-1">
        {!immutable && (
          <Icon
            color="text-gray-500 hover:text-red-600"
            dataLoc="enum-field-delete"
            icon={faTrashAlt}
            size="xs"
            onClick={() => onRemove(index)}
          />
        )}
      </div>
    </div>
  );
};
